import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { formatDistanceToNow } from "date-fns";
import { BookOpen, Copy, Globe2, Library, Lock } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { GlobalTemplatePreviewDialog } from "@/components/global-template-preview-dialog";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemHeader,
  ItemTitle,
} from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { getErrorMessage } from "@/lib/form-errors";
import { trpc, trpcClient } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type TemplateEntry = {
  template: {
    id: string;
    name: string;
    description: string | null;
    scope: "global" | "site";
    editable: boolean;
    updatedAt: string | Date;
    createdAt: string | Date;
    formType?: string;
    version?: string | null;
    isActive?: boolean | null;
  };
  site: { id: string; name: string | null } | null;
};

type CloneStarterPackResult =
  inferRouterOutputs<AppRouter>["forms"]["cloneStarterPack"];
type GlobalTemplateSummary =
  inferRouterOutputs<AppRouter>["forms"]["listGlobalTemplates"][number];

function resolveRequestedSiteId(
  managedSites: Array<{ id: string }> | undefined,
  requestedSiteId: string | null
) {
  if (!requestedSiteId || !managedSites) {
    return "";
  }

  return managedSites.some((site) => site.id === requestedSiteId)
    ? requestedSiteId
    : "";
}

function getTemplateCountLabel(count: number) {
  return `${count} template${count === 1 ? "" : "s"}`;
}

function LoadingSkeleton({ count = 3 }: { count?: number }) {
  const placeholderKeys = useMemo(
    () =>
      Array.from(
        { length: count },
        (_, index) => `template-skeleton-${index + 1}`
      ),
    [count]
  );

  return (
    <div className="space-y-3">
      {placeholderKeys.map((key) => (
        <div key={key} className="rounded-lg border bg-card p-4 shadow-sm">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="mt-3 h-3 w-1/2" />
        </div>
      ))}
    </div>
  );
}

function FiltersSection({
  selectedSiteId,
  onSiteChange,
  sites,
  searchTerm,
  onSearchChange,
  includeArchived,
  onIncludeArchivedChange,
}: {
  selectedSiteId: string;
  onSiteChange: (value: string) => void;
  sites: { id: string; name: string }[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  includeArchived: boolean;
  onIncludeArchivedChange: (checked: boolean) => void;
}) {
  return (
    <div className="grid gap-4 items-center sm:grid-cols-3">
      <div className="space-y-2">
        <Label
          htmlFor="manager-template-site"
          className="text-sm font-medium text-foreground"
        >
          Site
        </Label>
        <Select
          value={selectedSiteId}
          onValueChange={onSiteChange}
          disabled={sites.length === 0}
        >
          <SelectTrigger id="manager-template-site" aria-label="Select site">
            <SelectValue placeholder="Select a site" />
          </SelectTrigger>
          <SelectContent>
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>
                {site.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label
          htmlFor="manager-template-search"
          className="text-sm font-medium text-foreground"
        >
          Search
        </Label>
        <Input
          id="manager-template-search"
          placeholder="Search by name or description"
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>
      <div className="flex items-end pb-0.5">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="include-archived"
            checked={includeArchived}
            onCheckedChange={onIncludeArchivedChange}
          />
          <Label
            htmlFor="include-archived"
            className="text-sm text-foreground cursor-pointer"
          >
            Include archived
          </Label>
        </div>
      </div>
    </div>
  );
}

function LibrarySheet({
  open,
  onOpenChange,
  entries,
  selectedSiteId,
  onCloneTemplate,
  onPreviewTemplate,
  isCloning,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entries: TemplateEntry[];
  selectedSiteId: string;
  onCloneTemplate: (templateId: string) => void;
  onPreviewTemplate: (templateId: string) => void;
  isCloning: boolean;
}) {
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const term = search.trim().toLowerCase();
    return entries.filter(
      ({ template }) =>
        template.name.toLowerCase().includes(term) ||
        (template.description ?? "").toLowerCase().includes(term)
    );
  }, [entries, search]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col gap-0 p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b px-6 py-6">
          <div className="flex items-center gap-2">
            <Globe2 className="h-4 w-4 text-muted-foreground" />
            <SheetTitle>Library</SheetTitle>
          </div>
          <SheetDescription>
            Built-in presets maintained by Stonecut. Clone any preset to create
            an editable copy for your site.
          </SheetDescription>
          <div className="pt-1">
            <Input
              placeholder="Search presets…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No presets match your search.
            </p>
          ) : (
            <div className="space-y-3">
              {filtered.map(({ template }) => (
                <Item key={template.id} variant="outline">
                  <ItemHeader>
                    <ItemTitle className="flex items-center gap-2">
                      <Lock className="h-3 w-3 text-muted-foreground shrink-0" />
                      {template.name}
                    </ItemTitle>
                  </ItemHeader>
                  <ItemContent>
                    <ItemDescription>
                      {template.description || "No description provided."}
                    </ItemDescription>
                  </ItemContent>
                  <ItemActions>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onPreviewTemplate(template.id)}
                    >
                      Preview
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={isCloning || !selectedSiteId}
                      onClick={() => {
                        onCloneTemplate(template.id);
                      }}
                    >
                      Clone to site
                    </Button>
                  </ItemActions>
                </Item>
              ))}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 text-xs text-muted-foreground">
          {filtered.length} preset{filtered.length === 1 ? "" : "s"}
          {search.trim() ? " matching" : " available"}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function ManagerTemplatesIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const managedSites = useQuery(trpc.getManagedSites.queryOptions());
  const [searchTerm, setSearchTerm] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [starterPackConfirmOpen, setStarterPackConfirmOpen] = useState(false);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(
    null
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const requestedSiteId = searchParams.get("siteId");

  const selectedSiteId = useMemo(() => {
    const requestedSelection = resolveRequestedSiteId(
      managedSites.data,
      requestedSiteId
    );
    return requestedSelection || managedSites.data?.[0]?.id || "";
  }, [managedSites.data, requestedSiteId]);

  const cloneTemplateMutation = useMutation({
    ...trpc.forms.cloneTemplate.mutationOptions(),
    onSuccess: () => {
      toast.success("Template cloned to site");
      void templatesQuery.refetch();
      void starterPackPreviewQuery.refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const cloneStarterPackMutation = useMutation({
    mutationFn: async ({ siteId }: { siteId: string }) => {
      return (await trpcClient.forms.cloneStarterPack.mutate({
        siteId,
      })) as CloneStarterPackResult;
    },
    onSuccess: (result) => {
      const clonedCount = result.cloned.length;
      const skippedCount = result.skipped.length;

      if (clonedCount > 0 && skippedCount > 0) {
        toast.success(
          `Starter pack cloned: ${clonedCount} added, ${skippedCount} skipped`
        );
      } else if (clonedCount > 0) {
        toast.success(`Starter pack cloned: ${clonedCount} templates added`);
      } else {
        toast.message("Starter pack already present for this site");
      }

      void templatesQuery.refetch();
      void starterPackPreviewQuery.refetch();
    },
    onError: (error) => toast.error(error.message),
  });

  const templatesQuery = useQuery({
    ...trpc.forms.listTemplatesForManager.queryOptions({
      siteId: selectedSiteId,
      includeInactive: includeArchived,
    }),
    enabled: Boolean(selectedSiteId),
  });

  const globalTemplatesQuery = useQuery({
    ...trpc.forms.listGlobalTemplates.queryOptions(),
  });

  const starterPackPreviewQuery = useQuery({
    ...trpc.forms.listTemplatesForManager.queryOptions({
      siteId: selectedSiteId,
      includeInactive: true,
    }),
    enabled: Boolean(selectedSiteId),
  });

  const hasSites = managedSites.data && managedSites.data.length > 0;
  const selectedSite = managedSites.data?.find(
    (site) => site.id === selectedSiteId
  );

  const customTemplates = useMemo(() => {
    const entries = (templatesQuery.data ?? []) as TemplateEntry[];
    if (!searchTerm.trim()) return entries;
    const term = searchTerm.trim().toLowerCase();
    return entries.filter(({ template, site }) => {
      const siteName = site?.name?.toLowerCase() ?? "";
      return (
        template.name.toLowerCase().includes(term) ||
        (template.description ?? "").toLowerCase().includes(term) ||
        siteName.includes(term)
      );
    });
  }, [templatesQuery.data, searchTerm]);

  const libraryEntries = useMemo(
    () =>
      (globalTemplatesQuery.data ?? []).map((t) => ({
        template: {
          id: t.id,
          name: t.name,
          description: t.description,
          scope: "global" as const,
          editable: false,
          updatedAt: null as unknown as Date,
          createdAt: null as unknown as Date,
          formType: t.formType,
        },
        site: null,
      })) as TemplateEntry[],
    [globalTemplatesQuery.data]
  );

  const starterPackEntries = useMemo(
    () =>
      ((globalTemplatesQuery.data ?? []) as GlobalTemplateSummary[]).filter(
        (t) => t.starterPackDefault
      ),
    [globalTemplatesQuery.data]
  );

  const starterPackTemplatesToClone = useMemo(() => {
    const existingFormTypes = new Set(
      ((starterPackPreviewQuery.data ?? []) as TemplateEntry[])
        .filter((entry) => entry.template.scope === "site")
        .map((entry) => entry.template.formType?.trim())
        .filter((formType): formType is string => Boolean(formType))
    );
    return starterPackEntries.filter((t) => !existingFormTypes.has(t.formType));
  }, [starterPackEntries, starterPackPreviewQuery.data]);

  const starterPackSkippedCount =
    starterPackEntries.length - starterPackTemplatesToClone.length;

  const handleSiteChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set("siteId", value);
    } else {
      newParams.delete("siteId");
    }
    setSearchParams(newParams);
  };

  const handleCloneTemplate = (templateId: string) => {
    if (!selectedSiteId) return;
    cloneTemplateMutation.mutate({ templateId, siteId: selectedSiteId });
  };

  const handleCloneStarterPack = () => {
    if (!selectedSiteId) return;
    if (starterPackTemplatesToClone.length === 0) {
      toast.message("Starter pack already present for this site");
      return;
    }
    setStarterPackConfirmOpen(true);
  };

  const handleConfirmCloneStarterPack = () => {
    if (!selectedSiteId) return;
    setStarterPackConfirmOpen(false);
    cloneStarterPackMutation.mutate({ siteId: selectedSiteId });
  };

  const handlePreviewTemplate = (templateId: string) => {
    setPreviewTemplateId(templateId);
    setPreviewOpen(true);
  };

  const isTemplatesLoading =
    templatesQuery.isLoading || globalTemplatesQuery.isLoading;

  if (managedSites.isLoading) {
    return (
      <PageContainer className="space-y-4">
        <LoadingSkeleton />
      </PageContainer>
    );
  }

  if (managedSites.isError) {
    return (
      <PageContainer className="space-y-4">
        <FormErrorAlert
          message={getErrorMessage(managedSites.error, "Failed to load sites.")}
        />
      </PageContainer>
    );
  }

  if (isTemplatesLoading) {
    return (
      <PageContainer className="space-y-4">
        <LoadingSkeleton count={4} />
      </PageContainer>
    );
  }

  if (templatesQuery.isError || globalTemplatesQuery.isError) {
    return (
      <PageContainer className="space-y-4">
        <FormErrorAlert
          message={getErrorMessage(
            templatesQuery.error ?? globalTemplatesQuery.error,
            "Failed to load templates."
          )}
        />
      </PageContainer>
    );
  }

  if (!hasSites) {
    return (
      <PageContainer className="space-y-6">
        <PageHeader
          title="Templates"
          description="Manage the templates workers can use to submit forms on your sites."
        />
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No sites yet</EmptyTitle>
            <EmptyDescription>
              Templates will appear here once your first managed site is set up.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </PageContainer>
    );
  }

  return (
    <PageContainer className="space-y-8">
      <PageHeader
        title="Templates"
        description="Manage the templates workers can use to submit forms on your sites."
        toolbar={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLibraryOpen(true)}
          >
            <Library />
            Browse library
          </Button>
        }
      />

      {starterPackTemplatesToClone.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-lg border bg-card p-4">
          <div>
            <p className="text-sm font-medium">
              {customTemplates.length === 0
                ? "Get started with a starter pack"
                : `Add ${starterPackTemplatesToClone.length} more recommended templates`}
            </p>
            <p className="text-sm text-muted-foreground">
              Clones {starterPackTemplatesToClone.length} recommended presets to
              this site.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="shrink-0"
            onClick={handleCloneStarterPack}
            disabled={
              !selectedSiteId ||
              cloneStarterPackMutation.isPending ||
              starterPackPreviewQuery.isLoading
            }
          >
            <Copy />
            Clone starter pack
          </Button>
        </div>
      )}

      <div className="p-4 bg-card border border-border rounded-md">
        <FiltersSection
          selectedSiteId={selectedSiteId}
          onSiteChange={handleSiteChange}
          sites={managedSites.data ?? []}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          includeArchived={includeArchived}
          onIncludeArchivedChange={setIncludeArchived}
        />
      </div>

      {customTemplates.length === 0 ? (
        <Empty className="border-dashed">
          <EmptyHeader>
            <EmptyTitle className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              No templates yet
            </EmptyTitle>
            <EmptyDescription>
              {searchTerm.trim()
                ? "No templates match your search."
                : "Clone a preset from the library to make it available to workers on this site."}
            </EmptyDescription>
          </EmptyHeader>
          {!searchTerm.trim() && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLibraryOpen(true)}
            >
              <Library />
              Browse library
            </Button>
          )}
        </Empty>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <BookOpen className="h-4 w-4" />
            <span>Your templates ({customTemplates.length})</span>
          </div>
          {customTemplates.map(({ template }) => {
            const archived = template.isActive === false;
            const updatedAt = template.updatedAt
              ? formatDistanceToNow(new Date(template.updatedAt), {
                  addSuffix: true,
                })
              : null;

            return (
              <Link
                key={template.id}
                to={`/forms/templates/${template.id}?siteId=${selectedSiteId}`}
                className="block"
              >
                <Item
                  variant="outline"
                  className="cursor-pointer bg-card hover:bg-accent/50"
                >
                  <ItemHeader className="flex flex-col items-start sm:items-center sm:flex-row sm:justify-between gap-2">
                    <ItemTitle>
                      {archived && <Badge variant="outline">Archived</Badge>}
                      {template.name}
                    </ItemTitle>
                  </ItemHeader>
                  <ItemContent>
                    <ItemDescription>
                      {template.description || "No description provided."}
                    </ItemDescription>
                  </ItemContent>
                  <ItemFooter>
                    <div className="flex items-center gap-2">
                      {template.version && (
                        <span className="text-xs text-muted-foreground">
                          v{template.version}
                        </span>
                      )}
                      {updatedAt && (
                        <span className="text-xs text-muted-foreground">
                          · Updated {updatedAt}
                        </span>
                      )}
                    </div>
                  </ItemFooter>
                </Item>
              </Link>
            );
          })}
        </div>
      )}

      <LibrarySheet
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        entries={libraryEntries}
        selectedSiteId={selectedSiteId}
        onCloneTemplate={handleCloneTemplate}
        onPreviewTemplate={handlePreviewTemplate}
        isCloning={cloneTemplateMutation.isPending}
      />

      <GlobalTemplatePreviewDialog
        open={previewOpen}
        templateId={previewTemplateId}
        onOpenChange={(open) => {
          setPreviewOpen(open);
          if (!open) setPreviewTemplateId(null);
        }}
      />

      <ConfirmDialog
        open={starterPackConfirmOpen}
        onOpenChange={setStarterPackConfirmOpen}
        title="Clone starter pack?"
        description={
          <div className="space-y-3">
            <p>
              This will clone{" "}
              <span className="font-medium text-foreground">
                {getTemplateCountLabel(starterPackTemplatesToClone.length)}
              </span>{" "}
              to {selectedSite?.name ?? "this site"}.
            </p>
            <div>
              <p className="font-medium text-foreground">
                Templates about to be cloned:
              </p>
              <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-foreground">
                {starterPackTemplatesToClone.map((template) => (
                  <li key={template.id}>{template.name}</li>
                ))}
              </ul>
            </div>
            {starterPackSkippedCount > 0 && (
              <p>
                {getTemplateCountLabel(starterPackSkippedCount)} already exist
                for this site and will be skipped.
              </p>
            )}
          </div>
        }
        confirmLabel={`Clone ${getTemplateCountLabel(starterPackTemplatesToClone.length)}`}
        pendingLabel="Cloning starter pack..."
        pending={cloneStarterPackMutation.isPending}
        onConfirm={handleConfirmCloneStarterPack}
      />
    </PageContainer>
  );
}
